import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, DisabledText, ExternalLinkIcon, Select, Stack, SubHeadline } from "@KristianIV/common-ui";
import { ApplicationState } from "../reducers";
import { useAppDispatch } from "../storeHooks";
import { OrganisationInfo } from "../services/adminService";
import { getOperatingModes, setOperatingMode } from "../services/organisationService";
import { Status, StatusMessage } from "../status/status";
import { addPopupAction } from "./organisationReducer";
import styles from "./OrganisationPage.module.css";

export const firestoreModeUrl = (mode: string) =>
  `https://console.firebase.google.com/project/triangula-385d2/firestore/databases/-default-/data/~2Fenv~2Fprod~2Fapp~2Fconfiguration~2Fmodes~2F${mode}`;

const changedModeMessage = (mode: string, success: boolean): StatusMessage => ({
  id: "changed-mode",
  message: success ? `Changed to ${mode} mode` : `Failed to change to ${mode} mode`,
  component: "Operating mode",
  status: success ? Status.Ok : Status.Critical,
  useInPopup: true,
});

/** Mode picker for the organisation; changes go through the organisation user's JWT like before. */
const OperatingMode = ({ organisationInfo }: { organisationInfo: OrganisationInfo }) => {
  const dispatch = useAppDispatch();
  const organisationJwt = useSelector((state: ApplicationState) => state.organisation.userJwtToken);
  const [modes, setModes] = useState<string[]>([]);
  const [selectedMode, setSelectedMode] = useState(organisationInfo.mode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedMode(organisationInfo.mode);
  }, [organisationInfo.mode]);

  useEffect(() => {
    if (!organisationJwt) {
      return;
    }
    getOperatingModes(organisationJwt)
      .then(setModes)
      .catch(() => setModes([]));
  }, [organisationJwt]);

  const changeMode = (newMode: string) => {
    if (!organisationJwt) {
      return;
    }
    setSaving(true);
    setOperatingMode(newMode, organisationJwt)
      .then((success) => {
        if (success) {
          setSelectedMode(newMode);
        }
        dispatch(addPopupAction(changedModeMessage(newMode, success)));
      })
      .catch(() => dispatch(addPopupAction(changedModeMessage(newMode, false))))
      .finally(() => setSaving(false));
  };

  const options = (modes.length > 0 ? modes : [selectedMode]).map((mode) => ({ value: mode, label: mode }));

  return (
    <Card interactive={false} padded>
      <SubHeadline>Operating mode</SubHeadline>
      <Stack gap="md" className={styles.modeControls}>
        <Select
          aria-label="Operating mode"
          options={options}
          value={selectedMode}
          disabled={!organisationJwt || saving}
          onChange={(event) => changeMode(event.target.value)}
        />
        {!organisationJwt && <DisabledText>Waiting for the organisation login before the mode can be changed.</DisabledText>}
        <a href={firestoreModeUrl(selectedMode)} target="_blank" rel="noopener noreferrer" className={styles.modeLink}>
          <span>Open {selectedMode} mode in Firestore</span>
          <ExternalLinkIcon size={14} />
        </a>
      </Stack>
    </Card>
  );
};

export default OperatingMode;
